/**
 * @file
 * Active Filters chip removal behavior.
 *
 * Handles:
 * - Chip removal on server-rendered active filter chips.
 * - 300 ms debounced form submission when removing a filter chip.
 * - Scroll position preservation across AJAX refreshes.
 * - "Clear all filters" button interaction (also resets the investor toggle cookie).
 */

import { setInvestorMode } from '../../src/investor-mode.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Saved scroll position to restore after an AJAX refresh.
   */
  let savedScrollY: number | null = null;

  /**
   * Debounce delay (ms) for chip-removal form re-submissions.
   */
  const DEBOUNCE_DELAY = 300;

  /**
   * Debounce timer for chip-removal form submissions.
   */
  let chipDebounceTimer: number | null = null;

  // ---------------------------------------------------------------------------
  // Filter reset helpers
  // ---------------------------------------------------------------------------

  /**
   * Checks whether a select option represents the default "any" value.
   */
  function isDefaultOption(option: HTMLOptionElement): boolean {
    return option.value === '' || option.value === 'All';
  }

  /**
   * Resets a filter input to its default value.
   */
  function resetInput(input: HTMLInputElement | HTMLSelectElement): void {
    if (input instanceof HTMLSelectElement) {
      for (let i = 0; i < input.options.length; i++) {
        if (isDefaultOption(input.options[i])) {
          input.selectedIndex = i;
          return;
        }
      }
      input.selectedIndex = 0;
      return;
    }

    if (input.type === 'checkbox' || input.type === 'radio') {
      input.checked = false;
      return;
    }

    input.value = '';
  }

  /**
   * Finds form inputs matching a chip's data attributes and resets them.
   *
   * @param form - The exposed form element.
   * @param filterName - The exposed filter identifier (input name).
   * @param filterValue - The specific value to uncheck / deselect.
   * @param filterType - 'option' for selects/checkboxes/radios, 'text' for text inputs.
   */
  function resetFilterByChip(form: HTMLFormElement, filterName: string, filterValue: string, filterType: string): void {
    if (filterType === 'range') {
      // Numeric range filter — clear both min and max inputs.
      const minInput = form.querySelector<HTMLInputElement>(`input[name="${CSS.escape(filterName)}[min]"]`);
      const maxInput = form.querySelector<HTMLInputElement>(`input[name="${CSS.escape(filterName)}[max]"]`);
      if (minInput) {
        resetInput(minInput);
        minInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (maxInput) {
        resetInput(maxInput);
        maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (filterType === 'text') {
      // Text / search input — clear the value.
      const input = form.querySelector<HTMLInputElement>(`input[name="${CSS.escape(filterName)}"]`);
      if (input) {
        resetInput(input);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Select element.
    const select = form.querySelector<HTMLSelectElement>(`select[name="${CSS.escape(filterName)}"]`);
    if (select) {
      resetInput(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Checkbox or radio — the name in BEF checkboxes is typically
    // `identifier[value]`, so try both the raw name and the bracket form.
    const candidates = [
      `input[name="${CSS.escape(filterName)}"][value="${CSS.escape(filterValue)}"]`,
      `input[name="${CSS.escape(filterName)}[${CSS.escape(filterValue)}]"]`,
      `input[name="${CSS.escape(filterName + '[' + filterValue + ']')}"]`,
    ];

    for (const selector of candidates) {
      const input = form.querySelector<HTMLInputElement>(selector);
      if (input) {
        resetInput(input);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Unlock in-flight AJAX helper
  // ---------------------------------------------------------------------------

  /**
   * Resets the `ajaxing` lock on any in-flight Drupal AJAX instances
   * belonging to the given form so that the next submission is accepted.
   *
   * @param form - The exposed form element.
   */
  function unlockInFlightAjax(form: HTMLFormElement): void {
    const ajaxFn = (Drupal as unknown as Record<string, unknown>).ajax as
      { instances?: Array<{ element?: HTMLElement | false; ajaxing?: boolean } | null> } | undefined;

    if (!ajaxFn?.instances) return;

    ajaxFn.instances.forEach((instance) => {
      if (
        instance &&
        instance.element &&
        instance.element instanceof HTMLElement &&
        form.contains(instance.element) &&
        instance.ajaxing
      ) {
        instance.ajaxing = false;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Debounced form submission for chip removal
  // ---------------------------------------------------------------------------

  /**
   * Submits the exposed form after a 300 ms debounce.
   * Multiple rapid chip removals are batched into a single request.
   * Unlocks any in-flight AJAX so the new submission goes through.
   */
  function debouncedSubmit(form: HTMLFormElement): void {
    if (chipDebounceTimer) {
      clearTimeout(chipDebounceTimer);
    }

    chipDebounceTimer = window.setTimeout(() => {
      chipDebounceTimer = null;

      // Unlock any previous in-flight AJAX request.
      unlockInFlightAjax(form);

      // Show the non-blocking loading indicator.
      form.classList.add('views-exposed-form--loading');

      const submitBtn = form.querySelector<HTMLButtonElement | HTMLInputElement>(
        'input[type="submit"]:not([name="reset"]), button[type="submit"]:not([name="reset"])'
      );
      if (submitBtn) {
        submitBtn.dispatchEvent(new Event('click', { bubbles: true }));
      } else {
        form.requestSubmit();
      }
    }, DEBOUNCE_DELAY);
  }

  // ---------------------------------------------------------------------------
  // Active filter chips behavior (server-rendered, JS handles removal)
  // ---------------------------------------------------------------------------

  /**
   * Attaches click handlers to server-rendered active filter chips.
   *
   * @description
   * Chips are rendered in Twig from PHP-extracted filter data for fast LCP.
   * This behavior only wires up the removal interaction: clicking a chip
   * resets the corresponding form input and re-submits via debounce.
   */
  Drupal.behaviors.tpActiveFilters = {
    attach(context: Document | HTMLElement) {
      // Restore scroll position after an AJAX refresh.
      if (savedScrollY !== null) {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
        savedScrollY = null;
      }

      // Attach chip removal handlers.
      const chips = once('tp-active-filter-chip', '.active-filters__chip', context);

      chips.forEach((el) => {
        const chip = el as HTMLButtonElement;
        const form = chip.closest<HTMLFormElement>('form');
        if (!form) return;

        const filterName = chip.dataset.filterName || '';
        const filterValue = chip.dataset.filterValue || '';
        const filterType = chip.dataset.filterType || 'option';

        chip.addEventListener('click', () => {
          savedScrollY = window.scrollY;
          resetFilterByChip(form, filterName, filterValue, filterType);
          debouncedSubmit(form);
        });
      });

      // Attach "Clear all filters" handler.
      const clearBtns = once('tp-active-filter-clear-all', '.active-filters__clear-all', context);

      clearBtns.forEach((el) => {
        const btn = el as HTMLButtonElement;
        const form = btn.closest<HTMLFormElement>('form');
        if (!form) return;

        btn.addEventListener('click', () => {
          savedScrollY = window.scrollY;
          // Reset the investor mode cookie so the toggle is also cleared.
          setInvestorMode(false);
          // Reset all inputs in the form without triggering a page refresh.
          form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select').forEach((input) => {
            if (
              input.name === 'reset' ||
              input.name === 'form_build_id' ||
              input.name === 'form_id' ||
              input.name === 'form_token'
            ) {
              return;
            }
            resetInput(input as HTMLInputElement | HTMLSelectElement);
          });
          debouncedSubmit(form);
        });
      });
    },
  };
})(Drupal, once);
